import api from './client';

export interface OneCWarehouse {
  id: string;
  name: string;
}

export interface OneCStockBalance {
  item_id: string;
  item_name: string;
  warehouse_id: string;
  warehouse_name: string;
  in_stock: number;
  to_ship: number;
  available: number;
}

export interface OneCCatalogItem {
  id: string;
  name: string;
  article: string;
  price?: number;
  // Цена из клиентского прайса (OilChangePage.md rev. 2026-09-12 №19)
  price_from_price_list?: boolean;
  unit?: string;
  unit_name?: string;
}

export const getOneCWarehouses = (): Promise<OneCWarehouse[]> =>
  api.get<OneCWarehouse[]>('/1c/warehouses').then(r => r.data);

export const getOneCStockBalance = (params?: { warehouse_id?: string; item_id?: string }): Promise<OneCStockBalance[]> =>
  api.get<OneCStockBalance[]>('/1c/stock-balance', { params }).then(r => r.data);

export const getOneCCatalog = (params?: { search?: string; warehouse_id?: string; type?: string; id?: string }): Promise<OneCCatalogItem[]> =>
  api.get<OneCCatalogItem[]>('/1c/catalog', { params }).then(r => r.data);

export const getCatalogById = (id: string): Promise<OneCCatalogItem[]> =>
  api.get('/1c/catalog', { params: { id } }).then(r => r.data);

export interface OneCAcquiringTerminal {
  id: string;
  name: string;
  code?: string;
}

export const getOneCAcquiringTerminals = (): Promise<OneCAcquiringTerminal[]> =>
  api.get<OneCAcquiringTerminal[]>('/1c/acquiring-terminals').then(r => r.data);

// ───────────────────────────────────────────────
// Заказ-наряды 1С (бнНарядЗаказ) — полная структура
// ───────────────────────────────────────────────

export interface OneCServiceOrder {
  id: string;
  number: string;
  date: string;
  counterparty: string;
  amount: number;
  status: string;
  manager?: string;
}

/** Товар (ТЧ "Товары") */
export interface OneCServiceOrderProduct {
  line_number: number;
  nomenclature: string;     // Номенклатура.Наименование
  article?: string;         // Номенклатура.Артикул
  unit: string;             // Единица.Наименование
  quantity: number;
  price: number;
  sum: number;
  vat: number;              // НДС
  vat_rate: string;         // СтавкаНДС.Наименование
  sum_with_vat: number;     // СуммаСНДС
  warehouse: string;        // Склад.Наименование
  cash_register: string;    // Касса.Наименование
  remainder: number;        // Остаток
  do_not_print: boolean;    // НеПечатать
}

/** Услуга (ТЧ "Услуги") */
export interface OneCServiceOrderService {
  line_number: number;
  service_name: string;     // Услуга (строка) или УслугаСправочник.Наименование
  service_catalog: string;  // УслугаСправочник.Наименование (если заполнен)
  quantity: number;
  sum: number;
  vat: number;
  vat_rate: string;
  sum_with_vat: number;
  norm_hour: number;        // НормаЧас
  work_code: string;        // КодРаботы
  executor1: string;        // Исполнитель.Наименование
  executor2: string;        // Исполнитель2.Наименование
  executor3: string;        // Исполнитель3.Наименование
}

/** Связанный документ (ТЧ "СвязанныеДокументы") */
export interface OneCServiceOrderRelatedDoc {
  line_number: number;
  sales_doc: string;        // ДокументРеализации представление
}

/** Полный заказ-наряд (шапка + все ТЧ) */
export interface OneCServiceOrderDetail {
  // ── Шапка ──
  id: string;
  number: string;
  date: string;
  status: string;

  // Контрагент / клиент
  counterparty: string;
  retail_buyer_name: string;          // ФИОРозничногоПокупателя

  // Автомобиль
  vehicle: string;                    // Автомобиль.Наименование
  vin: string;
  license_plate: string;              // ГосНомер
  body_number: string;                // НомерКузова
  engine_number: string;              // НомерДвигателя
  engine_type: string;                // ТипДвигателя
  manufacture_year: number;           // ГодВыпуска
  color: string;                      // Цвет
  mileage: number;                    // Пробег
  purchase_date: string | null;       // ДатаПокупки
  repair_cost: number;                // СтоимостьПринятогоВремонтАвтомобиля

  // Организация / оплата
  organization: string;               // Организация.Наименование
  org_bank_account: string;           // БанковскийСчетОрганизации.Наименование
  payment_type: string;               // ВидОплаты
  card_payment: boolean;              // ОплатаКартой
  bank_terminal: string;              // БанковскийТерминал
  prepayment_sum: number;             // ПредоплатаСумма
  print_receipt: boolean;             // ПробиватьПоККТ

  // Работы
  work_start_date: string | null;     // ДатаНачалаРабот
  work_end_date: string | null;       // ДатаКонцаРабот
  norm_hour: number;                  // НормаЧас (шапка)
  regulatory_doc: string;             // НормативныйДокумент
  special_notes: string;              // ОсобыеОтметки
  recommendations: string;            // Рекомендации

  // Страховка
  insurer: string;                    // Страхователь.Наименование

  // НДС
  vat_for_products: boolean;          // НДСдляТоваров
  vat_for_services: boolean;          // НДСдляУслуг

  // Сумма
  amount: number;

  // ── Табличные части ──
  products: OneCServiceOrderProduct[];
  services: OneCServiceOrderService[];
  related_docs: OneCServiceOrderRelatedDoc[];
}

export const getOneCServiceOrders = (params?: { date_from?: string; date_to?: string; counterparty_name?: string }): Promise<OneCServiceOrder[]> =>
  api.get<OneCServiceOrder[]>('/1c/service-orders', { params }).then(r => r.data);

export const getOneCServiceOrder = (id: string): Promise<OneCServiceOrderDetail> =>
  api.get<OneCServiceOrderDetail>(`/1c/service-orders/${id}`).then(r => r.data);

export interface ServiceOrderCreatePayload {
  counterparty?: string;
  license_plate?: string;
  mileage?: number;
  products?: Partial<OneCServiceOrderProduct>[];
  services?: Partial<OneCServiceOrderService>[];
}

export const createOneCServiceOrder = (payload: ServiceOrderCreatePayload): Promise<OneCServiceOrder> =>
  api.post<OneCServiceOrder>('/1c/service-orders', payload).then(r => r.data);

// ───────────────────────────────────────────────
// ЧекККМ + маркировка
// ───────────────────────────────────────────────

export interface OneCCashReceiptItem {
  product_id: string;
  quantity: number;
  price: number;
  marking_code?: string;
}

export interface OneCCashReceiptCreate {
  organization_id?: string;
  warehouse_id?: string;
  cash_register_id?: string;
  items: OneCCashReceiptItem[];
}

export interface OneCCashReceipt {
  id: string;
  number: string;
  date: string;
  status: string;
  posted: boolean;
}

export interface OneCCashReceiptDetail {
  id: string;
  number: string;
  date: string;
  posted: boolean;
  organization: string;
  warehouse: string;
  cash_register: string;
  items: {
    line_number: number;
    product_id: string;
    product_name: string;
    quantity: number;
    price: number;
    sum: number;
    line_id: string;
  }[];
  marks: {
    line_id: string;
    marking_code: string;
    mark_id: string;
  }[];
}

export interface OneCMarkStatus {
  found: boolean;
  mark_id: string | null;
  code: string;
  product_id?: string;
  product_name?: string;
}

export const getOneCMarkStatus = (code: string): Promise<OneCMarkStatus> => {
  // Очищаем код от управляющих символов GS и префикса ]d2
  // перед отправкой в URL, т.к. nginx блокирует URL с %1D
  const cleanCode = code
    .replace(/\x1d/g, '')
    .replace(/\u001d/g, '')
    .replace(/^\]d2/, '');
  return api.get<OneCMarkStatus>('/1c/markstatus', { params: { code: cleanCode } }).then(r => r.data);
};

export interface MarkBalanceItem {
  marking_code: string;
  remaining_volume: number;
  status: string;
}

export const getMarkBalance = (product_id: string): Promise<{ marks: MarkBalanceItem[] }> =>
  api.get<{ marks: MarkBalanceItem[] }>('/1c/mark-balance', { params: { product_id } }).then(r => r.data);

export const createOneCCashReceipt = (payload: OneCCashReceiptCreate): Promise<OneCCashReceipt> =>
  api.post<OneCCashReceipt>('/1c/cash-receipts', payload).then(r => r.data);

export const getOneCCashReceipts = (params?: { date_from?: string; date_to?: string; number?: string }): Promise<OneCCashReceipt[]> =>
  api.get<OneCCashReceipt[]>('/1c/cash-receipts', { params }).then(r => r.data);

export const getOneCCashReceipt = (id: string): Promise<OneCCashReceiptDetail> =>
  api.get<OneCCashReceiptDetail>(`/1c/cash-receipts/${id}`).then(r => r.data);
